import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";

export default function PaymentSuccessPage() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const pollStatus = async () => {
      try {
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const resp = await axios.get(`${API}/payments/status/${sessionId}`, { headers, withCredentials: true });
        if (resp.data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (resp.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts < 5) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    pollStatus();
  }, [sessionId, token, attempts]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Confirming Payment</h2>
            <p className="text-sm text-zinc-400">Please wait while we verify your payment...</p>
          </div>
        )}
        {status === "success" && (
          <div className="space-y-4 fade-up">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-400" strokeWidth={2} />
            </div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Payment Successful</h2>
            <p className="text-sm text-zinc-400">Your plan has been upgraded. Enjoy enhanced protection!</p>
            <Link to="/dashboard">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-semibold mt-4" data-testid="payment-dashboard-btn">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <div className="space-y-4">
            <Shield className="w-12 h-12 text-zinc-600 mx-auto" />
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {status === "expired" ? "Session Expired" : "Payment Issue"}
            </h2>
            <p className="text-sm text-zinc-400">
              {status === "expired" ? "Your payment session has expired." : "We couldn't confirm your payment. Please check your email for confirmation."}
            </p>
            <Link to="/pricing">
              <Button className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-sm font-semibold mt-4" data-testid="payment-retry-btn">
                Try Again
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
